import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Users, 
  Calendar,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  PieChart,
  BarChart3,
  Layers,
  Target,
  Activity,
  Wallet,
  CreditCard,
  Receipt,
  Building2,
  Car,
  Wrench,
  Wifi,
  Zap,
  MoreHorizontal,
  FileText,
  Sparkles,
  Bell,
  Eye
} from 'lucide-react';
import { api } from '../services/api';

const DashboardPilotage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anneeScolaire, setAnneeScolaire] = useState(null);
  const [selectedView, setSelectedView] = useState('global');

  // Charger l'année scolaire
  useEffect(() => {
    const load = async () => {
      try {
        const result = await api.anneesScolaires.getActive();
        if (result.success && result.data) {
          setAnneeScolaire(result.data);
        }
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, []);

  // Charger les données du dashboard
 useEffect(() => {
  if (!anneeScolaire?.id) return;

  const load = async () => {
    try {
      setLoading(true);
      const result = await api.finance.getDashboardPilotage(anneeScolaire.id);
      
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  load();
}, [anneeScolaire]);

  // Formatage montants
  const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
  const fmtK = (n) => {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return Math.round(n / 1000) + 'K';
    return Math.round(n || 0).toString();
  };

  // Noms des mois français
  const moisFr = {
    1: 'Janvier', 2: 'Février', 3: 'Mars', 4: 'Avril',
    5: 'Mai', 6: 'Juin', 7: 'Juillet', 8: 'Août',
    9: 'Septembre', 10: 'Octobre', 11: 'Novembre', 12: 'Décembre'
  };

  // Icône par catégorie
  const getCategorieIcon = (categorie) => {
    const icons = {
      'Transport & Carburant': Car,
      'Maintenance & Réparations': Wrench,
      'Entretien & Nettoyage': Sparkles,
      'Abonnements & Services': Wifi,
      'Fournitures Bureau': FileText,
      'Électricité & Eau': Zap,
      'Autres Dépenses': MoreHorizontal
    };
    return icons[categorie] || MoreHorizontal;
  };

  // Couleur par catégorie
  const getCategorieColor = (categorie) => {
    const colors = {
      'Transport & Carburant': '#f59e0b',
      'Maintenance & Réparations': '#ef4444',
      'Entretien & Nettoyage': '#10b981',
      'Abonnements & Services': '#ec4899',
      'Fournitures Bureau': '#06b6d4',
      'Électricité & Eau': '#f97316',
      'Autres Dépenses': '#64748b'
    };
    return colors[categorie] || '#64748b';
  };

  // Loading
  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.loadingContainer}>
          <div style={S.loadingSpinner}>
            <Loader2 size={32} style={{animation: 'spin 1s linear infinite'}} color="#6366f1" />
          </div>
          <p style={S.loadingText}>Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error || !data) {
    return (
      <div style={S.page}>
        <div style={S.loadingContainer}>
          <AlertTriangle size={48} color="#ef4444" />
          <p style={{marginTop: 16, color: '#64748b'}}>{error || 'Aucune donnée disponible'}</p>
        </div>
      </div>
    );
  }

  const { encaissements, depenses, salaires, charges, resultats, alertes, comparaison_mensuelle } = data;

  return (
    <div style={S.page}>
      {/* ================================================================ */}
      {/* HEADER */}
      {/* ================================================================ */}
      <header style={S.header}>
        <div style={S.headerLeft}>
          <div style={S.brandIcon}>
            <Activity size={24} color="white" />
          </div>
          <div>
            <h1 style={S.brandTitle}>Centre de Pilotage Financier</h1>
            <span style={S.brandSub}>{data.annee_scolaire} • {data.effectif_total} élèves • {data.mois_ecoules} mois écoulés</span>
          </div>
        </div>

        <div style={S.headerRight}>
          <div style={S.viewTabs}>
            {[
              { key: 'global', label: 'Vue Globale', icon: Layers },
              { key: 'encaissements', label: 'Encaissements', icon: TrendingUp },
              { key: 'depenses', label: 'Dépenses', icon: Receipt },
              { key: 'salaires', label: 'Salaires', icon: Users }
            ].map(tab => (
              <button
                key={tab.key}
                style={selectedView === tab.key ? S.tabActive : S.tab}
                onClick={() => setSelectedView(tab.key)}
              >
                <tab.icon size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* ================================================================ */}
      {/* ALERTES */}
      {/* ================================================================ */}
      {alertes && alertes.length > 0 && (
        <div style={S.alertsBar}>
          <Bell size={18} color="#f59e0b" />
          <div style={S.alertsContent}>
            {alertes.slice(0, 3).map((alerte, i) => (
              <span key={i} style={{
                ...S.alertBadge,
                backgroundColor: alerte.type === 'danger' ? '#fee2e2' : alerte.type === 'warning' ? '#fef3c7' : '#e0f2fe',
                color: alerte.type === 'danger' ? '#991b1b' : alerte.type === 'warning' ? '#92400e' : '#0369a1'
              }}>
                {alerte.type === 'danger' && <XCircle size={14} />}
                {alerte.type === 'warning' && <AlertTriangle size={14} />}
                {alerte.type === 'info' && <Eye size={14} />}
                {alerte.message}
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={S.content}>
        {/* ================================================================ */}
        {/* VUE GLOBALE */}
        {/* ================================================================ */}
        {selectedView === 'global' && (
          <>
            {/* Hero KPIs */}
            <div style={S.heroGrid}>
              {/* CA Annuel */}
              <div style={S.heroCard}>
                <div style={{...S.heroGradient, background: 'radial-gradient(circle at top right, rgba(16, 185, 129, 0.15) 0%, transparent 60%)'}} />
                <div style={S.heroContent}>
                  <div style={S.heroTop}>
                    <div style={{...S.heroIcon, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
                      <TrendingUp size={24} color="white" />
                    </div>
                    <span style={S.heroLabel}>CA Annuel Projeté</span>
                  </div>
                  <div style={S.heroValue}>{fmt(encaissements.projection_annuelle)}</div>
                  <div style={S.heroUnit}>FCFA</div>
                  <div style={S.heroMeta}>
                    <span style={{...S.heroMetaValue, color: '#10b981'}}>↑ {encaissements.taux_recouvrement}%</span>
                    <span style={S.heroMetaLabel}>taux de recouvrement</span>
                  </div>
                </div>
              </div>

              {/* Charges Projetées */}
              <div style={S.heroCard}>
                <div style={{...S.heroGradient, background: 'radial-gradient(circle at top right, rgba(239, 68, 68, 0.15) 0%, transparent 60%)'}} />
                <div style={S.heroContent}>
                  <div style={S.heroTop}>
                    <div style={{...S.heroIcon, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>
                      <TrendingDown size={24} color="white" />
                    </div>
                    <span style={S.heroLabel}>Charges Projetées</span>
                  </div>
                  <div style={{...S.heroValue, color: '#ef4444'}}>{fmt(charges.projection_annuelle)}</div>
                  <div style={S.heroUnit}>FCFA</div>
                  <div style={S.heroMeta}>
                    <span style={{...S.heroMetaValue, color: charges.ratio_encaissements > 80 ? '#ef4444' : '#f59e0b'}}>
                      {charges.ratio_encaissements}%
                    </span>
                    <span style={S.heroMetaLabel}>des encaissements</span>
                  </div>
                </div>
              </div>

              {/* Résultat Projeté */}
              <div style={S.heroCard}>
                <div style={{...S.heroGradient, background: `radial-gradient(circle at top right, ${resultats.resultat_projete >= 0 ? 'rgba(99, 102, 241, 0.15)' : 'rgba(239, 68, 68, 0.15)'} 0%, transparent 60%)`}} />
                <div style={S.heroContent}>
                  <div style={S.heroTop}>
                    <div style={{...S.heroIcon, background: resultats.resultat_projete >= 0 
                      ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
                      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>
                      <Target size={24} color="white" />
                    </div>
                    <span style={S.heroLabel}>Résultat Projeté</span>
                  </div>
                  <div style={{...S.heroValue, color: resultats.resultat_projete >= 0 ? '#6366f1' : '#ef4444'}}>
                    {resultats.resultat_projete >= 0 ? '+' : ''}{fmt(resultats.resultat_projete)}
                  </div>
                  <div style={S.heroUnit}>FCFA</div>
                  <div style={S.heroMeta}>
                    <span style={{...S.heroMetaValue, color: resultats.marge_projetee >= 20 ? '#10b981' : resultats.marge_projetee >= 10 ? '#f59e0b' : '#ef4444'}}>
                      Marge: {resultats.marge_projetee}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Trésorerie Actuelle */}
              <div style={S.heroCard}>
                <div style={{...S.heroGradient, background: 'radial-gradient(circle at top right, rgba(139, 92, 246, 0.15) 0%, transparent 60%)'}} />
                <div style={S.heroContent}>
                  <div style={S.heroTop}>
                    <div style={{...S.heroIcon, background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'}}>
                      <Wallet size={24} color="white" />
                    </div>
                    <span style={S.heroLabel}>Solde Actuel</span>
                  </div>
                  <div style={{...S.heroValue, color: resultats.solde_actuel >= 0 ? '#8b5cf6' : '#ef4444'}}>
                    {resultats.solde_actuel >= 0 ? '+' : ''}{fmt(resultats.solde_actuel)}
                  </div>
                  <div style={S.heroUnit}>FCFA</div>
                  <div style={S.heroMeta}>
                    <span style={S.heroMetaLabel}>Encaissé - Dépensé</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Graphiques */}
            <div style={S.chartsGrid}>
              {/* Répartition des charges */}
              <div style={S.chartCard}>
                <div style={S.chartHeader}>
                  <PieChart size={20} color="#6366f1" />
                  <h3 style={S.chartTitle}>Répartition des Charges</h3>
                  <span style={S.chartSubtitle}>Total: {fmt(charges.total)} FCFA</span>
                </div>
                <div style={S.chartBody}>
                  <div style={S.donutContainer}>
                    <svg width="200" height="200" viewBox="0 0 200 200">
                      <defs>
                        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.1"/>
                        </filter>
                      </defs>
                      {/* Salaires Arc */}
                      <circle
                        cx="100" cy="100" r="75"
                        fill="none"
                        stroke="#6366f1"
                        strokeWidth="24"
                        strokeDasharray={`${charges.repartition.salaires.pourcentage * 4.71} 471`}
                        strokeLinecap="round"
                        transform="rotate(-90 100 100)"
                        filter="url(#shadow)"
                      />
                      {/* Dépenses Arc */}
                      <circle
                        cx="100" cy="100" r="75"
                        fill="none"
                        stroke="#f59e0b"
                        strokeWidth="24"
                        strokeDasharray={`${charges.repartition.depenses.pourcentage * 4.71} 471`}
                        strokeDashoffset={`${-charges.repartition.salaires.pourcentage * 4.71}`}
                        strokeLinecap="round"
                        transform="rotate(-90 100 100)"
                        filter="url(#shadow)"
                      />
                      {/* Centre */}
                      <circle cx="100" cy="100" r="55" fill="white" />
                      <text x="100" y="92" textAnchor="middle" style={{fontSize: '11px', fill: '#94a3b8', fontWeight: '500'}}>TOTAL</text>
                      <text x="100" y="112" textAnchor="middle" style={{fontSize: '18px', fontWeight: '700', fill: '#1e293b'}}>
                        {fmtK(charges.total)}
                      </text>
                    </svg>
                  </div>
                  <div style={S.legendList}>
                    <div style={S.legendItem}>
                      <div style={{...S.legendDot, backgroundColor: '#6366f1'}} />
                      <div style={S.legendInfo}>
                        <span style={S.legendLabel}>Salaires</span>
                        <span style={S.legendValue}>{fmt(charges.repartition.salaires.montant)} FCFA</span>
                      </div>
                      <span style={{...S.legendPct, color: '#6366f1'}}>{charges.repartition.salaires.pourcentage}%</span>
                    </div>
                    <div style={S.legendItem}>
                      <div style={{...S.legendDot, backgroundColor: '#f59e0b'}} />
                      <div style={S.legendInfo}>
                        <span style={S.legendLabel}>Autres dépenses</span>
                        <span style={S.legendValue}>{fmt(charges.repartition.depenses.montant)} FCFA</span>
                      </div>
                      <span style={{...S.legendPct, color: '#f59e0b'}}>{charges.repartition.depenses.pourcentage}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Évolution mensuelle */}
              <div style={S.chartCard}>
                <div style={S.chartHeader}>
                  <BarChart3 size={20} color="#6366f1" />
                  <h3 style={S.chartTitle}>Encaissements vs Dépenses</h3>
                  <span style={S.chartSubtitle}>Par mois</span>
                </div>
                <div style={S.chartBody}>
                  <div style={S.barsContainer}>
                    {comparaison_mensuelle.map((mois, i) => {
                      const maxVal = Math.max(...comparaison_mensuelle.map(m => Math.max(m.encaissements || 0, m.depenses || 0)));
                      const encH = maxVal > 0 ? ((mois.encaissements || 0) / maxVal) * 100 : 0;
                      const depH = maxVal > 0 ? ((mois.depenses || 0) / maxVal) * 100 : 0;
                      
                      return (
                        <div key={i} style={S.barGroup}>
                          <div style={S.barTooltip}>
                            <span style={{color: '#10b981'}}>{fmtK(mois.encaissements || 0)}</span>
                            <span style={{color: '#ef4444'}}>{fmtK(mois.depenses || 0)}</span>
                          </div>
                          <div style={S.barsWrapper}>
                            <div style={{...S.bar, height: `${encH}%`, background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)'}} />
                            <div style={{...S.bar, height: `${depH}%`, background: 'linear-gradient(180deg, #ef4444 0%, #dc2626 100%)'}} />
                          </div>
                          <span style={S.barLabel}>{mois.mois?.substring(0, 3) || ''}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={S.barLegend}>
                    <div style={S.barLegendItem}>
                      <div style={{...S.barLegendDot, background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)'}} />
                      <span>Encaissements</span>
                    </div>
                    <div style={S.barLegendItem}>
                      <div style={{...S.barLegendDot, background: 'linear-gradient(180deg, #ef4444 0%, #dc2626 100%)'}} />
                      <span>Dépenses</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tableau comparatif */}
            <div style={S.tableCard}>
              <div style={S.tableHeader}>
                <Eye size={20} color="#6366f1" />
                <h3 style={S.tableTitle}>Analyse Mensuelle Détaillée</h3>
              </div>
              <div style={S.tableWrapper}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Mois</th>
                      <th style={S.thR}>Encaissements</th>
                      <th style={S.thR}>Dépenses</th>
                      <th style={S.thR}>Solde du Mois</th>
                      <th style={S.thC}>Ratio Dép/Enc</th>
                      <th style={S.thC}>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparaison_mensuelle.map((mois, i) => (
                      <tr key={i} style={S.tr}>
                        <td style={S.td}>
                          <div style={S.moisCell}>
                            <span style={S.moisNum}>{mois.ordre}</span>
                            <strong>{mois.mois}</strong>
                          </div>
                        </td>
                        <td style={S.tdR}>
                          <span style={{color: '#10b981', fontWeight: '600'}}>{fmt(mois.encaissements)} FCFA</span>
                        </td>
                        <td style={S.tdR}>
                          <span style={{color: '#ef4444', fontWeight: '600'}}>{fmt(mois.depenses)} FCFA</span>
                        </td>
                        <td style={S.tdR}>
                          <span style={{
                            color: mois.solde >= 0 ? '#10b981' : '#ef4444', 
                            fontWeight: '700',
                            backgroundColor: mois.solde >= 0 ? '#dcfce7' : '#fee2e2',
                            padding: '4px 10px',
                            borderRadius: '6px'
                          }}>
                            {mois.solde >= 0 ? '+' : ''}{fmt(mois.solde)} FCFA
                          </span>
                        </td>
                        <td style={S.tdC}>
                          <div style={S.ratioCell}>
                            <div style={S.ratioBar}>
                              <div style={{
                                ...S.ratioFill,
                                width: `${Math.min(mois.ratio, 100)}%`,
                                backgroundColor: mois.ratio <= 50 ? '#10b981' : mois.ratio <= 80 ? '#f59e0b' : '#ef4444'
                              }} />
                            </div>
                            <span style={{
                              color: mois.ratio <= 50 ? '#166534' : mois.ratio <= 80 ? '#92400e' : '#991b1b',
                              fontWeight: '600'
                            }}>
                              {mois.ratio}%
                            </span>
                          </div>
                        </td>
                        <td style={S.tdC}>
                          {mois.solde >= 0 ? (
                            <div style={{...S.statusBadge, backgroundColor: '#dcfce7', color: '#166534'}}>
                              <CheckCircle size={16} />
                              <span>Excédent</span>
                            </div>
                          ) : (
                            <div style={{...S.statusBadge, backgroundColor: '#fee2e2', color: '#991b1b'}}>
                              <AlertTriangle size={16} />
                              <span>Déficit</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ================================================================ */}
        {/* VUE ENCAISSEMENTS */}
        {/* ================================================================ */}
        {selectedView === 'encaissements' && (
          <>
            <div style={S.kpisGrid}>
              <div style={S.kpiCard}>
                <div style={{...S.kpiIcon, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
                  <DollarSign size={24} color="white" />
                </div>
                <div style={S.kpiContent}>
                  <span style={S.kpiLabel}>Total Encaissé</span>
                  <span style={{...S.kpiValue, color: '#10b981'}}>{fmt(encaissements.total_encaisse)}</span>
                  <span style={S.kpiUnit}>FCFA</span>
                </div>
              </div>

              <div style={S.kpiCard}>
                <div style={{...S.kpiIcon, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'}}>
                  <Target size={24} color="white" />
                </div>
                <div style={S.kpiContent}>
                  <span style={S.kpiLabel}>Total Attendu</span>
                  <span style={S.kpiValue}>{fmt(encaissements.total_attendu)}</span>
                  <span style={S.kpiUnit}>FCFA</span>
                </div>
              </div>

              <div style={S.kpiCard}>
                <div style={{...S.kpiIcon, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                  <Activity size={24} color="white" />
                </div>
                <div style={S.kpiContent}>
                  <span style={S.kpiLabel}>Taux de Recouvrement</span>
                  <span style={{...S.kpiValue, color: encaissements.taux_recouvrement >= 70 ? '#10b981' : '#f59e0b'}}>
                    {encaissements.taux_recouvrement}%
                  </span>
                  <div style={S.progressMini}>
                    <div style={{...S.progressMiniFill, width: `${encaissements.taux_recouvrement}%`}} />
                  </div>
                </div>
              </div>

              <div style={S.kpiCard}>
                <div style={{...S.kpiIcon, background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'}}>
                  <TrendingUp size={24} color="white" />
                </div>
                <div style={S.kpiContent}>
                  <span style={S.kpiLabel}>CA Annuel Projeté</span>
                  <span style={S.kpiValue}>{fmt(encaissements.projection_annuelle)}</span>
                  <span style={S.kpiUnit}>FCFA</span>
                </div>
              </div>
            </div>

            <div style={S.tableCard}>
              <div style={S.tableHeader}>
                <Calendar size={20} color="#6366f1" />
                <h3 style={S.tableTitle}>Détail des Encaissements par Mois</h3>
              </div>
              <div style={S.tableWrapper}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Mois</th>
                      <th style={S.thR}>Attendu</th>
                      <th style={S.thR}>Encaissé</th>
                      <th style={S.thR}>Restant</th>
                      <th style={S.thC}>Taux</th>
                      <th style={{...S.th, width: '200px'}}>Progression</th>
                    </tr>
                  </thead>
                  <tbody>
                    {encaissements.par_mois.map((mois, i) => {
                      const restant = (mois.attendu || 0) - (mois.paye || 0);
                      return (
                        <tr key={i} style={S.tr}>
                          <td style={S.td}><strong>{mois.mois}</strong></td>
                          <td style={S.tdR}>{fmt(mois.attendu)} FCFA</td>
                          <td style={{...S.tdR, color: '#10b981', fontWeight: '600'}}>{fmt(mois.paye)} FCFA</td>
                          <td style={{...S.tdR, color: '#f59e0b', fontWeight: '600'}}>{fmt(restant)} FCFA</td>
                          <td style={S.tdC}>
                            <span style={{
                              ...S.tauxBadge,
                              backgroundColor: mois.taux >= 70 ? '#dcfce7' : mois.taux >= 40 ? '#fef3c7' : '#fee2e2',
                              color: mois.taux >= 70 ? '#166534' : mois.taux >= 40 ? '#92400e' : '#991b1b'
                            }}>
                              {mois.taux}%
                            </span>
                          </td>
                          <td style={S.tdC}>
                            <div style={S.progressBarContainer}>
                              <div style={{
                                ...S.progressBarFill,
                                width: `${mois.taux}%`,
                                background: mois.taux >= 70 
                                  ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                                  : mois.taux >= 40
                                    ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
                                    : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'
                              }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ================================================================ */}
        {/* VUE DÉPENSES */}
        {/* ================================================================ */}
        {selectedView === 'depenses' && (
          <>
            <div style={S.kpisGrid}>
              <div style={S.kpiCard}>
                <div style={{...S.kpiIcon, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>
                  <Receipt size={24} color="white" />
                </div>
                <div style={S.kpiContent}>
                  <span style={S.kpiLabel}>Total Dépenses</span>
                  <span style={{...S.kpiValue, color: '#ef4444'}}>{fmt(depenses.total)}</span>
                  <span style={S.kpiUnit}>FCFA ({depenses.nombre_transactions} opérations)</span>
                </div>
              </div>

              <div style={S.kpiCard}>
                <div style={{...S.kpiIcon, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                  <Calendar size={24} color="white" />
                </div>
                <div style={S.kpiContent}>
                  <span style={S.kpiLabel}>Moyenne Mensuelle</span>
                  <span style={S.kpiValue}>{fmt(depenses.moyenne_mensuelle)}</span>
                  <span style={S.kpiUnit}>FCFA / mois</span>
                </div>
              </div>

              <div style={S.kpiCard}>
                <div style={{...S.kpiIcon, background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'}}>
                  <TrendingUp size={24} color="white" />
                </div>
                <div style={S.kpiContent}>
                  <span style={S.kpiLabel}>Projection Annuelle</span>
                  <span style={S.kpiValue}>{fmt(depenses.projection_annuelle)}</span>
                  <span style={S.kpiUnit}>FCFA</span>
                </div>
              </div>

              <div style={S.kpiCard}>
                <div style={{...S.kpiIcon, background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)'}}>
                  <FileText size={24} color="white" />
                </div>
                <div style={S.kpiContent}>
                  <span style={S.kpiLabel}>Ratio / Encaissements</span>
                  <span style={{
                    ...S.kpiValue, 
                    color: charges.ratio_encaissements <= 50 ? '#10b981' : charges.ratio_encaissements <= 80 ? '#f59e0b' : '#ef4444'
                  }}>
                    {charges.ratio_encaissements}%
                  </span>
                </div>
              </div>
            </div>

            <div style={S.chartsGrid}>
              {/* Par catégorie */}
              <div style={S.chartCard}>
                <div style={S.chartHeader}>
                  <PieChart size={20} color="#6366f1" />
                  <h3 style={S.chartTitle}>Répartition par Catégorie</h3>
                </div>
                <div style={S.chartBody}>
                  {depenses.par_categorie && depenses.par_categorie.map((cat, i) => {
                    const Icon = getCategorieIcon(cat.categorie);
                    const color = getCategorieColor(cat.categorie);
                    
                    return (
                      <div key={i} style={S.categoryRow}>
                        <div style={S.categoryLeft}>
                          <div style={{...S.categoryIcon, backgroundColor: color + '20'}}>
                            <Icon size={18} color={color} />
                          </div>
                          <div style={S.categoryInfo}>
                            <span style={S.categoryName}>{cat.categorie}</span>
                            <span style={S.categoryCount}>{cat.nombre} transactions</span>
                          </div>
                        </div>
                        <div style={S.categoryRight}>
                          <span style={S.categoryAmount}>{fmt(cat.total)} FCFA</span>
                          <span style={{...S.categoryPct, color: color}}>{cat.pourcentage}%</span>
                        </div>
                        <div style={S.categoryBarTrack}>
                          <div style={{...S.categoryBarFill, width: `${cat.pourcentage}%`, backgroundColor: color}} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top dépenses */}
              <div style={S.chartCard}>
                <div style={S.chartHeader}>
                  <AlertTriangle size={20} color="#f59e0b" />
                  <h3 style={S.chartTitle}>Top 10 Dépenses</h3>
                </div>
                <div style={S.chartBody}>
                  <div style={S.topList}>
                    {depenses.top_10 && depenses.top_10.map((dep, i) => (
                      <div key={i} style={S.topItem}>
                        <div style={{...S.topRank, backgroundColor: i < 3 ? '#fee2e2' : '#f1f5f9', color: i < 3 ? '#991b1b' : '#64748b'}}>
                          {i + 1}
                        </div>
                        <div style={S.topInfo}>
                          <span style={S.topDesc}>{dep.description}</span>
                          <span style={S.topDate}>
                            {new Date(dep.date).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <span style={S.topAmount}>{fmt(dep.montant)} FCFA</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ================================================================ */}
        {/* VUE SALAIRES */}
        {/* ================================================================ */}
        {selectedView === 'salaires' && (
          <>
            <div style={S.kpisGrid}>
              <div style={S.kpiCard}>
                <div style={{...S.kpiIcon, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'}}>
                  <Users size={24} color="white" />
                </div>
                <div style={S.kpiContent}>
                  <span style={S.kpiLabel}>Total Salaires Versés</span>
                  <span style={S.kpiValue}>{fmt(salaires.total)}</span>
                  <span style={S.kpiUnit}>FCFA</span>
                </div>
              </div>

              <div style={S.kpiCard}>
                <div style={{...S.kpiIcon, background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'}}>
                  <Building2 size={24} color="white" />
                </div>
                <div style={S.kpiContent}>
                  <span style={S.kpiLabel}>Enseignants</span>
                  <span style={{...S.kpiValue, color: '#8b5cf6'}}>{fmt(salaires.total_enseignants)}</span>
                  <span style={S.kpiUnit}>{salaires.nombre_enseignants} personnes</span>
                </div>
              </div>

              <div style={S.kpiCard}>
                <div style={{...S.kpiIcon, background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)'}}>
                  <CreditCard size={24} color="white" />
                </div>
                <div style={S.kpiContent}>
                  <span style={S.kpiLabel}>Personnel Administratif</span>
                  <span style={{...S.kpiValue, color: '#ec4899'}}>{fmt(salaires.total_employes)}</span>
                  <span style={S.kpiUnit}>{salaires.nombre_employes} personnes</span>
                </div>
              </div>

              <div style={S.kpiCard}>
                <div style={{...S.kpiIcon, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                  <Calendar size={24} color="white" />
                </div>
                <div style={S.kpiContent}>
                  <span style={S.kpiLabel}>Masse Salariale Moyenne</span>
                  <span style={S.kpiValue}>{fmt(salaires.moyenne_mensuelle)}</span>
                  <span style={S.kpiUnit}>FCFA / mois</span>
                </div>
              </div>
            </div>

            <div style={S.chartsGrid}>
              {/* Enseignants */}
              <div style={S.tableCard}>
                <div style={S.tableHeader}>
                  <Building2 size={20} color="#8b5cf6" />
                  <h3 style={S.tableTitle}>Salaires Enseignants</h3>
                </div>
                <div style={S.tableWrapper}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Période</th>
                        <th style={S.thR}>Brut</th>
                        <th style={S.thR}>Retenues</th>
                        <th style={S.thR}>Avances</th>
                        <th style={S.thR}>Net</th>
                        <th style={S.thC}>Payés</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaires.enseignants_par_mois && salaires.enseignants_par_mois.slice(0, 6).map((s, i) => (
                        <tr key={i} style={S.tr}>
                          <td style={S.td}><strong>{moisFr[s.mois]} {s.annee}</strong></td>
                          <td style={S.tdR}>{fmt(s.brut)}</td>
                          <td style={{...S.tdR, color: '#ef4444'}}>{fmt(s.retenues)}</td>
                          <td style={{...S.tdR, color: '#f59e0b'}}>{fmt(s.avances)}</td>
                          <td style={{...S.tdR, color: '#10b981', fontWeight: '700'}}>{fmt(s.net)}</td>
                          <td style={S.tdC}>
                            <span style={S.countBadge}>{s.nombre}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Personnel */}
              <div style={S.tableCard}>
                <div style={S.tableHeader}>
                  <Users size={20} color="#ec4899" />
                  <h3 style={S.tableTitle}>Salaires Personnel</h3>
                </div>
                <div style={S.tableWrapper}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Période</th>
                        <th style={S.thR}>Brut</th>
                        <th style={S.thR}>Retenues</th>
                        <th style={S.thR}>Net</th>
                        <th style={S.thC}>Payés</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaires.employes_par_mois && salaires.employes_par_mois.slice(0, 6).map((s, i) => (
                        <tr key={i} style={S.tr}>
                          <td style={S.td}><strong>{moisFr[s.mois]} {s.annee}</strong></td>
                          <td style={S.tdR}>{fmt(s.brut)}</td>
                          <td style={{...S.tdR, color: '#ef4444'}}>{fmt(s.retenues)}</td>
                          <td style={{...S.tdR, color: '#10b981', fontWeight: '700'}}>{fmt(s.net)}</td>
                          <td style={S.tdC}>
                            <span style={S.countBadge}>{s.nombre}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Projection */}
            <div style={S.projectionCard}>
              <div style={S.projectionLeft}>
                <div style={S.projectionIcon}>
                  <TrendingUp size={28} color="white" />
                </div>
                <div>
                  <h3 style={S.projectionTitle}>Projection Masse Salariale Annuelle</h3>
                  <p style={S.projectionDesc}>
                    Basé sur une moyenne mensuelle de <strong>{fmt(salaires.moyenne_mensuelle)} FCFA</strong> × {data.nombre_mois} mois
                  </p>
                </div>
              </div>
              <div style={S.projectionRight}>
                <span style={S.projectionAmount}>{fmt(salaires.projection_annuelle)}</span>
                <span style={S.projectionUnit}>FCFA / an</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// STYLES
// ============================================================================

const S = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    width: '100%'
  },
  content: {
    padding: '24px',
    maxWidth: '1600px',
    margin: '0 auto'
  },

  // Loading
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80vh'
  },
  loadingSpinner: {
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(99, 102, 241, 0.15)'
  },
  loadingText: {
    marginTop: 16,
    color: '#64748b',
    fontSize: '14px'
  },

  // Header
  header: {
    backgroundColor: 'white',
    borderBottom: '1px solid #e2e8f0',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px',
    position: 'sticky',
    top: 0,
    zIndex: 100
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  brandIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
  },
  brandTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    letterSpacing: '-0.3px'
  },
  brandSub: {
    fontSize: '13px',
    color: '#64748b'
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center'
  },
  viewTabs: {
    display: 'flex',
    backgroundColor: '#f1f5f9',
    borderRadius: '12px',
    padding: '4px',
    gap: '4px'
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    color: '#64748b',
    transition: 'all 0.2s'
  },
  tabActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    border: 'none',
    backgroundColor: 'white',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    color: '#6366f1',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },

  // Alerts
  alertsBar: {
    backgroundColor: '#fffbeb',
    borderBottom: '1px solid #fef3c7',
    padding: '12px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  alertsContent: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    flex: 1
  },
  alertBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500'
  },

  // Hero Grid
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px',
    marginBottom: '24px'
  },
  heroCard: {
    position: 'relative',
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden'
  },
  heroGradient: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    pointerEvents: 'none'
  },
  heroContent: {
    position: 'relative',
    zIndex: 1
  },
  heroTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px'
  },
  heroIcon: {
    width: '52px',
    height: '52px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
  },
  heroLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#64748b'
  },
  heroValue: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    letterSpacing: '-1px',
    lineHeight: 1.1
  },
  heroUnit: {
    fontSize: '13px',
    color: '#94a3b8',
    marginBottom: '12px'
  },
  heroMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  heroMetaValue: {
    fontSize: '13px',
    fontWeight: '700'
  },
  heroMetaLabel: {
    fontSize: '12px',
    color: '#94a3b8'
  },

  // Charts Grid
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '20px',
    marginBottom: '24px'
  },
  chartCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden'
  },
  chartHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px 20px',
    borderBottom: '1px solid #f1f5f9'
  },
  chartTitle: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e293b',
    flex: 1
  },
  chartSubtitle: {
    fontSize: '12px',
    color: '#94a3b8'
  },
  chartBody: {
    padding: '20px'
  },

  // Donut
  donutContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px'
  },
  legendList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '10px'
  },
  legendDot: {
    width: '14px',
    height: '14px',
    borderRadius: '4px',
    flexShrink: 0
  },
  legendInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  legendLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#1e293b'
  },
  legendValue: {
    fontSize: '12px',
    color: '#64748b'
  },
  legendPct: {
    fontSize: '16px',
    fontWeight: '700'
  },

  // Bars
  barsContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    height: '180px',
    gap: '8px',
    marginBottom: '16px',
    paddingTop: '30px'
  },
  barGroup: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    position: 'relative'
  },
  barTooltip: {
    position: 'absolute',
    top: '-25px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontSize: '10px',
    fontWeight: '600',
    opacity: 0.7
  },
  barsWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '4px',
    height: '140px'
  },
  bar: {
    width: '18px',
    borderRadius: '4px 4px 0 0',
    transition: 'height 0.5s ease',
    minHeight: '4px'
  },
  barLabel: {
    fontSize: '11px',
    color: '#64748b',
    fontWeight: '500'
  },
  barLegend: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    paddingTop: '12px',
    borderTop: '1px solid #f1f5f9'
  },
  barLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '500'
  },
  barLegendDot: {
    width: '12px',
    height: '12px',
    borderRadius: '3px'
  },

  // KPIs Grid
  kpisGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '24px'
  },
  kpiCard: {
    backgroundColor: 'white',
    borderRadius: '14px',
    padding: '20px',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  kpiIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
  },
  kpiContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1
  },
  kpiLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '500'
  },
  kpiValue: {
    fontSize: '26px',
    fontWeight: '700',
    color: '#1e293b',
    letterSpacing: '-0.5px'
  },
  kpiUnit: {
    fontSize: '11px',
    color: '#94a3b8'
  },
  progressMini: {
    width: '100%',
    height: '6px',
    backgroundColor: '#e2e8f0',
    borderRadius: '3px',
    marginTop: '8px',
    overflow: 'hidden'
  },
  progressMiniFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
    borderRadius: '3px'
  },

  // Table
  tableCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    marginBottom: '24px'
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px 20px',
    borderBottom: '1px solid #f1f5f9'
  },
  tableTitle: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e293b'
  },
  tableWrapper: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px'
  },
  th: {
    padding: '14px 16px',
    textAlign: 'left',
    backgroundColor: '#f8fafc',
    color: '#64748b',
    fontWeight: '600',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #e2e8f0'
  },
  thR: {
    padding: '14px 16px',
    textAlign: 'right',
    backgroundColor: '#f8fafc',
    color: '#64748b',
    fontWeight: '600',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #e2e8f0'
  },
  thC: {
    padding: '14px 16px',
    textAlign: 'center',
    backgroundColor: '#f8fafc',
    color: '#64748b',
    fontWeight: '600',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #e2e8f0'
  },
  tr: {
    borderBottom: '1px solid #f1f5f9',
    transition: 'background-color 0.15s'
  },
  td: {
    padding: '14px 16px',
    color: '#475569'
  },
  tdR: {
    padding: '14px 16px',
    textAlign: 'right',
    color: '#475569'
  },
  tdC: {
    padding: '14px 16px',
    textAlign: 'center'
  },
  moisCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  moisNum: {
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    backgroundColor: '#f1f5f9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: '600',
    color: '#64748b'
  },
  ratioCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    justifyContent: 'center'
  },
  ratioBar: {
    width: '60px',
    height: '8px',
    backgroundColor: '#f1f5f9',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  ratioFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s'
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600'
  },
  tauxBadge: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '700'
  },
  countBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: '#ede9fe',
    color: '#6366f1',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600'
  },
  progressBarContainer: {
    width: '100%',
    height: '10px',
    backgroundColor: '#f1f5f9',
    borderRadius: '5px',
    overflow: 'hidden'
  },
  progressBarFill: {
    height: '100%',
    borderRadius: '5px',
    transition: 'width 0.5s ease'
  },

  // Categories
  categoryRow: {
    marginBottom: '16px'
  },
  categoryLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px'
  },
  categoryIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  categoryInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  categoryName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b'
  },
  categoryCount: {
    fontSize: '11px',
    color: '#94a3b8'
  },
  categoryRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
    justifyContent: 'flex-end'
  },
  categoryAmount: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b'
  },
  categoryPct: {
    fontSize: '14px',
    fontWeight: '700'
  },
  categoryBarTrack: {
    height: '8px',
    backgroundColor: '#f1f5f9',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  categoryBarFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.5s ease'
  },

  // Top list
  topList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  topItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '10px'
  },
  topRank: {
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '700'
  },
  topInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  topDesc: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#1e293b'
  },
  topDate: {
    fontSize: '11px',
    color: '#94a3b8'
  },
  topAmount: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#ef4444'
  },

  // Projection card
  projectionCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    borderRadius: '16px',
    padding: '24px',
    color: 'white'
  },
  projectionLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  projectionIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  projectionTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '4px'
  },
  projectionDesc: {
    margin: 0,
    fontSize: '13px',
    color: '#94a3b8'
  },
  projectionRight: {
    textAlign: 'right'
  },
  projectionAmount: {
    display: 'block',
    fontSize: '32px',
    fontWeight: '700',
    letterSpacing: '-1px'
  },
  projectionUnit: {
    fontSize: '13px',
    color: '#94a3b8'
  }
};

// CSS Animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  tbody tr:hover {
    background-color: #f8fafc !important;
  }
`;
if (!document.querySelector('#dashboard-pilotage-styles')) {
  styleSheet.id = 'dashboard-pilotage-styles';
  document.head.appendChild(styleSheet);
}

export default DashboardPilotage;